<template>
  <div class="page">
    <header class="topbar">
      <div class="brand brand-link" @click="goDashboard">
        <img class="brand-mark" :src="brandLogo" alt="Decast" />
        <span class="brand-name">Decast</span>
        <span class="brand-sep">/</span>
        <span class="brand-crumb">Cast Statistics</span>
      </div>
      <HostPolicyPill v-if="sessionKind !== 'room'" :value="accessPolicy" />
    </header>

    <div v-if="loggedIn" class="usage-line">
      <PlanHint variant="post-call" />
    </div>

    <main class="grid">
      <SessionCard
        :ended-by="endedByName"
        :meta="sessionMetaLine"
        :initial-rating="rating"
        :show-detailed-feedback="loggedIn"
        :back-label="loggedIn ? 'Back to Dashboard' : 'Back to Decast'"
        @feedback="openFeedback"
        @back="goDashboard"
        @rate="onRate"
      />

      <section class="panel">
        <div v-if="analyticsLoading && !showLocked" class="loading">
          <div class="spinner"></div>
          <p>Preparing your meeting summary…</p>
        </div>

        <div v-else-if="analyticsError === 'unauthorized'" class="empty">
          <p>Session expired. Redirecting to login…</p>
        </div>

        <LockedView
          v-else-if="showLocked && stage === 'locked'"
          :logged-in="loggedIn"
          @verify="stage = 'verify'"
          @open-feedback="stage = 'locked-feedback'"
          @request-access="onRequestEmailAccess"
        />

        <VerifyView
          v-else-if="showLocked && stage === 'verify'"
          :credentials="requiredCredentials"
          @back="stage = 'locked'"
          @present="onPresentCredentials"
        />

        <div
          v-else-if="showLocked && stage === 'locked-feedback'"
          class="locked-feedback"
        >
          <button
            type="button"
            class="locked-feedback-back"
            @click="stage = 'locked'"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Back
          </button>
          <h2 class="locked-feedback-title">Decast platform feedback</h2>
          <p class="locked-feedback-sub">
            Help us improve the platform. Sent to the Decast team, not the host.
          </p>
          <FeedbackTab
            :initial-rating="rating"
            :already-submitted="feedbackSubmitted"
            :existing="feedbackExisting"
            @submit="onFeedbackSubmit"
          />
        </div>

        <UnlockedView
          v-else
          :active-tab="activeTab"
          :cast-title="castTitle"
          :session-kind="sessionKind"
          :duration-label="durationLabel"
          :participant-count="participantCount"
          :host-name="hostName"
          :ended-ago="endedAgoLabel"
          :ended-on="endedOn"
          :sorted-users="sortedUsers"
          :poll-list="pollList"
          :presentation-list="presentationList"
          :transcript-ready="transcriptReady"
          :recording-ready="recordingReady"
          :insights-ready="insightsReady"
          :transcript-word-count="transcriptWordCount"
          :transcript-speakers="transcriptSpeakers"
          :insights-count="insightsCount"
          :rating="rating"
          :logged-in="loggedIn"
          :feedback-submitted="feedbackSubmitted"
          :feedback-existing="feedbackExisting"
          :recording="recording"
          :recording-pending="recordingEnabled && !recording"
          :recording-blocked="recordingBlocked"
          :public-meeting-id="publicMeetingId"
          @tab="activeTab = $event"
          @notify="onNotify"
          @feedback-submit="onFeedbackSubmit"
        />
      </section>
    </main>
  </div>
</template>

<script>
import meetingAnalyticsMixin from '@/mixins/meetingAnalytics.js';
import SessionCard from './meeting-ended/SessionCard.vue';
import PlanHint from './components/PlanHint.vue';
import HostPolicyPill from './meeting-ended/HostPolicyPill.vue';
import LockedView from './meeting-ended/LockedView.vue';
import VerifyView from './meeting-ended/VerifyView.vue';
import UnlockedView from './meeting-ended/UnlockedView.vue';
import FeedbackTab from './meeting-ended/FeedbackTab.vue';
import { groupPresentations } from './meeting-ended/helpers.js';
import newLogo from '@/assets/images/newLogo.svg';

export default {
  name: 'MeetingEnded',
  components: {
    SessionCard,
    PlanHint,
    HostPolicyPill,
    LockedView,
    VerifyView,
    UnlockedView,
    FeedbackTab,
  },
  mixins: [meetingAnalyticsMixin],
  data() {
    return {
      brandLogo: newLogo,
      activeTab: 'Users',
      accessPolicy: 'public_all',
      isHost: true,
      viewerHasVc: false,
      stage: 'locked',
      rating: 0,
      loggedIn: !!localStorage.getItem('accessToken'),
      feedbackSubmitted: false,
      feedbackExisting: null,
      recordingPollAttempts: 0,
      recordingPollTimer: null,
      requiredCredentials: [
        {
          id: 'press',
          name: 'Accredited Press Member',
          issuer: 'Press Council',
          found: true,
        },
        {
          id: 'reporter',
          name: 'Decast Verified Reporter',
          issuer: 'Decast Trust System',
          found: true,
        },
      ],
    };
  },
  computed: {
    analyticsId() {
      return this.$route.query.id || this.$route.query.meetingId;
    },
    showLocked() {
      if (this.isHost) return false;
      return this.accessPolicy === 'private_vc' && !this.viewerHasVc;
    },
    endedOn() {
      return (this.analyticsData && this.analyticsData.endedOn) || 0;
    },
    castTitle() {
      const name = this.analyticsData && this.analyticsData.name;
      const label = this.sessionKind === 'room' ? 'Room' : 'Cast';
      return name ? `${label}: ${name}` : `${label} Statistics`;
    },
    sessionMetaLine() {
      if (!this.analyticsData) return 'Awaiting session summary…';
      const parts = [];
      if (this.analyticsData.name) parts.push(this.analyticsData.name);
      if (this.durationMin) parts.push(`${this.durationMin} min`);
      if (this.participantCount) {
        parts.push(
          `${this.participantCount} participant${
            this.participantCount === 1 ? '' : 's'
          }`
        );
      }
      return parts.join(' · ');
    },
    durationMin() {
      if (!this.analyticsData) return 0;
      const ms =
        (this.analyticsData.endedOn || 0) - (this.analyticsData.createdOn || 0);
      return Math.max(0, Math.round(ms / 60000));
    },
    durationLabel() {
      return this.durationMin ? `${this.durationMin} min` : '-';
    },
    userList() {
      if (!this.analyticsData || !this.analyticsData.users) return [];
      return Object.values(this.analyticsData.users);
    },
    sortedUsers() {
      return [...this.userList].sort((a, b) => {
        const ta = (a.talk && a.talk.totalTime) || 0;
        const tb = (b.talk && b.talk.totalTime) || 0;
        return tb - ta;
      });
    },
    participantCount() {
      return this.userList.length;
    },
    hostName() {
      const host = this.userList.find((u) => u.isModerator);
      return host ? host.name : '';
    },
    endedByName() {
      if (this.hostName) return this.hostName;
      return this.analyticsData ? 'the host' : 'host';
    },
    pollList() {
      if (!this.analyticsData || !this.analyticsData.polls) return [];
      // BBB's learning-dashboard JSON keeps the question on the poll but
      // the votes on each user (users[x].answers[pollId] = [option]), and
      // anonymous responses on the poll itself. Join them into the
      // {key, numVotes} rows PollsTab renders.
      const users = Object.values(this.analyticsData.users || {});
      return Object.values(this.analyticsData.polls).map((poll) => {
        const tally = {};
        (poll.options || []).forEach((opt) => {
          tally[opt] = 0;
        });
        let respondents = 0;
        users.forEach((user) => {
          const picked = (user.answers || {})[poll.pollId];
          if (!picked || !picked.length) return;
          respondents += 1;
          picked.forEach((opt) => {
            tally[opt] = (tally[opt] || 0) + 1;
          });
        });
        (poll.anonymousAnswers || []).forEach((raw) => {
          const opt = raw && raw.answer ? raw.answer : raw;
          if (!opt) return;
          tally[opt] = (tally[opt] || 0) + 1;
          respondents += 1;
        });
        return {
          ...poll,
          answers: Object.keys(tally).map((key, i) => ({
            id: `${poll.pollId}-${i}`,
            key,
            numVotes: tally[key],
          })),
          numRespondents: respondents,
        };
      });
    },
    presentationList() {
      return groupPresentations(
        this.analyticsData && this.analyticsData.presentationSlides
      );
    },
    endedAgoLabel() {
      if (!this.endedOn) return '';
      const diffMs = Date.now() - this.endedOn;
      const min = Math.max(0, Math.round(diffMs / 60000));
      if (min < 1) return 'Session ended just now';
      if (min < 60) return `Session ended ${min} min ago`;
      const h = Math.round(min / 60);
      return `Session ended ${h}h ago`;
    },
    transcriptReady() {
      return false;
    },
    recordingReady() {
      return false;
    },
    insightsReady() {
      return !!(this.analyticsData && this.analyticsData.users);
    },
    transcriptWordCount() {
      return '-';
    },
    transcriptSpeakers() {
      return this.participantCount || '-';
    },
    insightsCount() {
      return this.pollList.length + this.presentationList.length;
    },
  },
  watch: {
    analyticsError(val) {
      if (val === 'unauthorized') {
        setTimeout(() => this.$router.push('/welcome'), 1200);
      } else if (
        val === 'missing_analytics_id' ||
        val === 'missing_meeting_id' ||
        val === 'not_found'
      ) {
        setTimeout(() => this.$router.push('/dashboard'), 1500);
      }
    },
    accessPolicy() {
      this.stage = 'locked';
    },
    // Once the analytics endpoint returns we know the cast's public
    // meeting id. Fire the secondary lookups (access policy + feedback
    // status) only then so they hit the right cast key.
    publicMeetingId(slug) {
      if (!slug) return;
      this.loadAccessPolicy(slug);
      if (this.loggedIn) this.loadFeedbackStatus(slug);
    },
    analyticsData(val) {
      if (val && this.sessionKind === 'room' && this.loggedIn) {
        this.loadFeedbackStatus(null);
      }
      // Analytics arrives before BBB finishes processing the recording.
      // Once we have analytics but recording is still null, refetch
      // every 60s for up to 5 minutes to surface the recording when
      // BBB publishes it.
      if (val && !this.recording) this.startRecordingPoll();
    },
    recording(val) {
      // Stop polling once recording lands.
      if (val) this.stopRecordingPoll();
    },
  },
  beforeDestroy() {
    this.stopRecordingPoll();
  },
  mounted() {
    if (!this.analyticsId) {
      this.analyticsLoading = false;
      this.analyticsError = 'missing_analytics_id';
      return;
    }
    this.startAnalyticsPolling(this.analyticsId);
  },
  methods: {
    startRecordingPoll() {
      this.stopRecordingPoll();
      this.recordingPollAttempts = 0;
      const tick = async () => {
        if (this.recording) return;
        if (this.recordingPollAttempts >= 15) return; // 15 × 60s = 15 min
        this.recordingPollAttempts += 1;
        await this.refetchAnalytics(this.analyticsId);
        if (!this.recording) {
          this.recordingPollTimer = setTimeout(tick, 60000);
        }
      };
      this.recordingPollTimer = setTimeout(tick, 60000);
    },
    stopRecordingPoll() {
      if (this.recordingPollTimer) {
        clearTimeout(this.recordingPollTimer);
        this.recordingPollTimer = null;
      }
    },
    async loadAccessPolicy(slug) {
      const policyState = await this.fetchAccessPolicy(slug);
      if (!policyState) return;
      if (policyState.policy) this.accessPolicy = policyState.policy;
      if (typeof policyState.is_host === 'boolean') {
        this.isHost = policyState.is_host;
      }
      if (typeof policyState.has_access === 'boolean') {
        this.viewerHasVc = policyState.has_access;
      }
      if (Array.isArray(policyState.required_credentials)) {
        this.requiredCredentials = policyState.required_credentials.map(
          (c) => ({
            id: c.id || c.name,
            name: c.name,
            issuer: c.issuer,
            found: !!c.found,
          })
        );
      }
    },
    async loadFeedbackStatus(slug) {
      const fb = await this.getPostCallFeedbackStatus(slug);
      if (fb && fb.submitted) {
        this.feedbackSubmitted = true;
        this.feedbackExisting = fb.feedback || null;
      }
    },
    onRate(n) {
      this.rating = n;
    },
    openFeedback() {
      this.activeTab = 'Feedback';
      this.$nextTick(() => {
        const panel = this.$el && this.$el.querySelector('.panel');
        if (panel && panel.scrollIntoView) {
          panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },
    async onFeedbackSubmit(payload, done) {
      try {
        await this.submitPostCallFeedback(this.publicMeetingId, payload);
        this.feedbackSubmitted = true;
        this.feedbackExisting = {
          rating: payload.rating,
          message: payload.message,
          anonymous: payload.anonymous,
          created_at: new Date().toISOString(),
        };
        this.$vs &&
          this.$vs.notify &&
          this.$vs.notify({
            title: 'Feedback sent',
            text: 'Thanks for helping us improve Decast.',
            color: 'success',
          });
        if (done) done(true);
      } catch (err) {
        const httpStatus = err && err.response && err.response.status;
        if (httpStatus === 409) {
          this.feedbackSubmitted = true;
          this.$vs &&
            this.$vs.notify &&
            this.$vs.notify({
              title: 'Already submitted',
              text: 'You have already sent feedback for this cast.',
              color: 'warning',
            });
        } else if (httpStatus === 401) {
          this.$vs &&
            this.$vs.notify &&
            this.$vs.notify({
              title: 'Sign in required',
              text: 'Please sign in to send feedback.',
              color: 'danger',
            });
        } else {
          this.$vs &&
            this.$vs.notify &&
            this.$vs.notify({
              title: 'Could not send feedback',
              text: 'Please try again.',
              color: 'danger',
            });
        }
        if (done) done(false);
      }
    },
    goDashboard() {
      if (this.loggedIn) {
        this.$router.push('/dashboard');
      } else {
        window.location.assign('https://decast.live');
      }
    },
    async onRequestEmailAccess(email, done) {
      try {
        await this.requestVcAccessApi(this.publicMeetingId, email);
        this.$vs &&
          this.$vs.notify &&
          this.$vs.notify({
            title: 'Request sent',
            text: `Host will be notified at ${email}.`,
            color: 'success',
          });
      } catch (e) {
        this.$vs &&
          this.$vs.notify &&
          this.$vs.notify({
            title: 'Could not send request',
            text: 'Please try again.',
            color: 'danger',
          });
      }
      if (done) done();
    },
    async onPresentCredentials(done) {
      try {
        // Real flow would build a verifiable presentation via the
        // DID extension, then post the VP JWT to the backend for
        // verification. Stub: resolve a fake VP and let the backend
        // tell us if access is granted.
        const vpJwt = 'stub.vp.jwt';
        const res = await this.presentCredentialsApi(
          this.publicMeetingId,
          vpJwt
        );
        const granted = !!(res && res.data && res.data.granted);
        if (granted) {
          this.viewerHasVc = true;
          this.startAnalyticsPolling(this.analyticsId);
          this.$vs &&
            this.$vs.notify &&
            this.$vs.notify({
              title: 'Credentials accepted',
              text: 'Access unlocked.',
              color: 'success',
            });
        } else {
          this.$vs &&
            this.$vs.notify &&
            this.$vs.notify({
              title: 'Verification failed',
              text: 'Required credentials were not accepted.',
              color: 'danger',
            });
        }
      } catch (e) {
        this.$vs &&
          this.$vs.notify &&
          this.$vs.notify({
            title: 'Verification error',
            text: 'Could not verify credentials.',
            color: 'danger',
          });
      }
      if (done) done();
    },
    onNotify(email) {
      this.$vs &&
        this.$vs.notify &&
        this.$vs.notify({
          title: 'Notification queued',
          text: `Recording link will be sent to ${email}.`,
          color: 'success',
        });
    },
  },
};
</script>

<style scoped>
.usage-line {
  /* its own strip between the header and the two columns, never a column itself */
  padding: 0 0 10px;
}
.page {
  min-height: 100vh;
  background: #07080d;
  color: #fff;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  padding: 22px 32px 60px;
  overflow-x: hidden;
}
.page,
.page *,
.page *::before,
.page *::after {
  box-sizing: border-box;
}
.page input,
.page textarea,
.page button {
  max-width: 100%;
}
@media (max-width: 768px) {
  .page {
    padding: 16px 14px 40px;
  }
}

.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 4px 0 22px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  margin-bottom: 26px;
  flex-wrap: wrap;
}
.brand {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
}
.brand-mark {
  width: 32px;
  height: 32px;
  display: block;
  flex-shrink: 0;
  border-radius: 7px;
}
.brand-name {
  letter-spacing: 0.01em;
}
.brand-sep {
  color: rgba(255, 255, 255, 0.3);
  font-weight: 400;
}
.brand-crumb {
  color: rgba(255, 255, 255, 0.65);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
@media (max-width: 540px) {
  .topbar {
    gap: 10px;
    padding: 4px 0 16px;
    margin-bottom: 18px;
  }
  .brand {
    gap: 8px;
    font-size: 14px;
    min-width: 0;
    flex: 1;
  }
  .brand-mark {
    width: 28px;
    height: 28px;
  }
  .brand-sep,
  .brand-crumb {
    display: none;
  }
}

.grid {
  display: grid;
  grid-template-columns: 360px minmax(0, 1fr);
  gap: 22px;
  align-items: start;
}
.grid > * {
  min-width: 0;
}
@media (max-width: 1024px) {
  .grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.panel {
  background: #0e0f17;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  padding: 28px 32px;
  min-height: 480px;
  min-width: 0;
  overflow: hidden;
}
@media (max-width: 768px) {
  .panel {
    padding: 22px 18px;
  }
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 1.5rem;
  gap: 1rem;
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
}
.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(211, 202, 87, 0.2);
  border-top-color: #d3ca57;
  border-radius: 50%;
  animation: me-spin 0.9s linear infinite;
}
@keyframes me-spin {
  to {
    transform: rotate(360deg);
  }
}
.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 1.5rem;
  color: rgba(255, 255, 255, 0.4);
  text-align: center;
}

.locked-feedback {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
}
.locked-feedback-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.65);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 0;
  margin-bottom: 18px;
  font-family: inherit;
  transition: color 0.15s ease;
}
.locked-feedback-back:hover {
  color: #fff;
}
.locked-feedback-title {
  font-size: 22px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 6px;
  line-height: 1.3;
}
.locked-feedback-sub {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);
  margin: 0 0 22px;
  line-height: 1.55;
}
.brand-link {
  cursor: pointer;
}
</style>

<style>
:root {
  --me-bg: #07080d;
  --me-card: #0e0f17;
  --me-card-2: #131522;
  --me-accent: #d3ca57;
}

.stat-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
}
.stat-list::-webkit-scrollbar {
  width: 4px;
}
.stat-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 2px;
}

.stat-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: rgba(255, 255, 255, 0.02);
  padding: 0.85rem 1rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.04);
  color: #fff;
  font-size: 14px;
  transition: background 0.15s ease;
}
.stat-row:hover {
  background: rgba(255, 255, 255, 0.04);
}
.stat-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #3a4565;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
  flex-shrink: 0;
  text-transform: uppercase;
}
.stat-label {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.stat-label strong {
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}
.stat-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 3px;
  flex-wrap: wrap;
}
.stat-meta-item {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  gap: 3px;
}
.stat-badge {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 10px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}
.stat-badge--moderator {
  background: #d3ca57;
  color: #000;
}
.stat-badge--viewer {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.6);
}
.stat-value {
  color: #d3ca57;
  font-weight: 700;
  font-size: 13px;
  white-space: nowrap;
  background: rgba(211, 202, 87, 0.1);
  padding: 3px 8px;
  border-radius: 6px;
}
.poll-card {
  background: rgba(255, 255, 255, 0.02);
  border-radius: 10px;
  padding: 1rem 1.1rem;
  border: 1px solid rgba(255, 255, 255, 0.04);
}
.poll-question {
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 0.7rem;
  line-height: 1.4;
}
.poll-answer-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 7px;
}
.poll-answer-row:last-of-type {
  margin-bottom: 0;
}
.poll-answer-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  width: 90px;
  min-width: 60px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 0;
}
.poll-answer-track {
  flex: 1;
  height: 6px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 3px;
  overflow: hidden;
}
.poll-answer-fill {
  height: 100%;
  background: #d3ca57;
  border-radius: 3px;
  transition: width 0.4s ease;
}
.poll-answer-count {
  font-size: 12px;
  font-weight: 600;
  color: #d3ca57;
  min-width: 22px;
  text-align: right;
}
.poll-footer {
  margin-top: 0.6rem;
  padding-top: 0.6rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);
}
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 1.5rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.3);
  font-family: 'JetBrains Mono', ui-monospace, monospace;
}
.empty-state svg {
  margin-bottom: 0.75rem;
  opacity: 0.4;
}
.empty-state p {
  font-size: 14px;
  margin: 0;
  line-height: 1.5;
}
</style>
